/**
 * Base API client.
 *
 * All server communication funnels through this module so we can
 * attach auth headers, handle token refresh, and normalise errors
 * in one place.
 */

import { Capacitor } from '@capacitor/core';
import type { ApiResponse, AuthTokens } from '@/types/api';
import {
  breadcrumb,
  captureFailure,
  warn,
} from '@/lib/appTelemetry';

/** Skip gzip on Android — WebView can mishandle compressed POST bodies, resulting in empty requests. */
const SKIP_GZIP_ON_ANDROID = Capacitor.getPlatform() === 'android';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

/** GET request timeout (ms) so snapshot/track loads don't hang indefinitely */
const GET_REQUEST_TIMEOUT_MS = 15_000;

function apiPath(path: string): string {
  const q = path.indexOf('?');
  return q >= 0 ? path.slice(0, q) : path;
}

function reportHttpFailure(
  method: string,
  path: string,
  status: number,
): void {
  const data = { method, path: apiPath(path), status };
  if (status >= 500) {
    captureFailure('api', `HTTP ${status}`, undefined, data);
  } else if (status >= 400) {
    warn('api', `HTTP ${status}`, data);
  }
}

function reportSessionExpired(path: string): void {
  captureFailure('api', 'session expired', undefined, {
    path: apiPath(path),
  });
}

function reportNetworkError(method: string, path: string, message: string): void {
  captureFailure('api', 'network error', undefined, {
    method,
    path: apiPath(path),
    message,
  });
}

export function getApiBaseUrl(): string {
  return BASE_URL;
}

let tokens: AuthTokens | null = null;

/** Callback invoked when the session is fully invalidated (refresh failed on a 401). */
let onSessionExpired: (() => void) | null = null;

export function setOnSessionExpired(cb: () => void) {
  onSessionExpired = cb;
}

/** Inject tokens after login / restore from storage */
export function setTokens(t: AuthTokens | null) {
  tokens = t;
}

export function getTokens(): AuthTokens | null {
  return tokens;
}

export function isAuthenticated(): boolean {
  return (
    tokens !== null &&
    typeof tokens.accessToken === 'string' &&
    tokens.accessToken.length > 0 &&
    typeof tokens.expiresAt === 'number' &&
    tokens.expiresAt > Date.now()
  );
}

/**
 * Try to refresh the access token using the stored refresh token.
 * Returns true on success, false on failure.
 */
async function tryRefreshToken(): Promise<boolean> {
  if (!tokens?.refreshToken) return false;

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: tokens.refreshToken }),
    });

    const json = await res.json();

    if (json.success && json.data?.accessToken) {
      tokens = {
        ...tokens,
        accessToken: json.data.accessToken,
        expiresAt: json.data.expiresAt,
      };
      // Persist updated tokens
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Compress a string to gzip using CompressionStream (if available).
 * Returns the compressed ArrayBuffer, or null if compression is unavailable.
 */
async function gzipString(input: string): Promise<ArrayBuffer | null> {
  if (typeof CompressionStream === 'undefined') return null;

  const encoder = new TextEncoder();
  const data = encoder.encode(input);

  const cs = new CompressionStream('gzip');
  const writer = cs.writable.getWriter();
  const reader = cs.readable.getReader();

  writer.write(data);
  writer.close();

  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    totalLength += value.byteLength;
  }

  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }

  return result.buffer;
}

/** Threshold (bytes) above which we gzip request bodies */
const GZIP_THRESHOLD = 1024;

/** Low-level fetch wrapper with auth + JSON handling + gzip compression */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { signal?: AbortSignal },
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Encoding': 'gzip',
  };

  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  // Prepare request body, compressing large payloads with gzip
  // Skip gzip on Android: WebView can drop or corrupt compressed POST bodies, causing validation errors
  let requestBody: BodyInit | undefined;
  if (body) {
    const jsonStr = JSON.stringify(body);
    const shouldCompress =
      !SKIP_GZIP_ON_ANDROID &&
      jsonStr.length > GZIP_THRESHOLD;

    if (shouldCompress) {
      const compressed = await gzipString(jsonStr);
      if (compressed) {
        requestBody = compressed;
        headers['Content-Encoding'] = 'gzip';
      } else {
        requestBody = jsonStr;
      }
    } else {
      requestBody = jsonStr;
    }
  }

  const controller = new AbortController();
  const timeoutId =
    method === 'GET'
      ? setTimeout(() => controller.abort(), GET_REQUEST_TIMEOUT_MS)
      : undefined;

  const onExternalAbort = () => controller.abort();
  options?.signal?.addEventListener('abort', onExternalAbort, { once: true });

  if (options?.signal?.aborted) {
    if (timeoutId) clearTimeout(timeoutId);
    return { success: false, error: { code: 'ABORTED', message: 'Request aborted' } };
  }

  try {
    let res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: requestBody,
      signal: controller.signal,
    });

    if (timeoutId) clearTimeout(timeoutId);

    let sessionExpired = false;

    // Handle 401 — try token refresh and retry once
    if (res.status === 401 && tokens?.refreshToken) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${tokens!.accessToken}`;
        const retryController = new AbortController();
        const retryTimeoutId =
          method === 'GET'
            ? setTimeout(() => retryController.abort(), GET_REQUEST_TIMEOUT_MS)
            : undefined;
        res = await fetch(`${BASE_URL}${path}`, {
          method,
          headers,
          body: requestBody,
          signal: retryController.signal,
        });
        if (retryTimeoutId) clearTimeout(retryTimeoutId);
      } else {
        tokens = null;
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('user_profile');
        sessionExpired = true;
        reportSessionExpired(path);
        if (onSessionExpired) onSessionExpired();
      }
    }

    if (res.status >= 400 && !sessionExpired) {
      reportHttpFailure(method, path, res.status);
    }

    const json = await res.json();
    return json as ApiResponse<T>;
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err?.name === 'AbortError') {
      breadcrumb('api', 'request aborted', { method, path: apiPath(path) });
      return { success: false, error: { code: 'ABORTED', message: 'Request aborted' } };
    }
    reportNetworkError(method, path, err.message ?? 'Network error');
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err.message ?? 'Network error' },
    };
  } finally {
    options?.signal?.removeEventListener('abort', onExternalAbort);
  }
}

/** Low-level fetch wrapper for multipart/form-data requests. */
async function requestForm<T>(
  method: string,
  path: string,
  formData: FormData,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Accept-Encoding': 'gzip',
  };

  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  try {
    let res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: formData,
    });

    let sessionExpired = false;

    if (res.status === 401 && tokens?.refreshToken) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        headers['Authorization'] = `Bearer ${tokens!.accessToken}`;
        res = await fetch(`${BASE_URL}${path}`, {
          method,
          headers,
          body: formData,
        });
      } else {
        tokens = null;
        localStorage.removeItem('auth_tokens');
        localStorage.removeItem('user_profile');
        sessionExpired = true;
        reportSessionExpired(path);
        if (onSessionExpired) onSessionExpired();
      }
    }

    if (res.status >= 400 && !sessionExpired) {
      reportHttpFailure(method, path, res.status);
    }

    const json = await res.json();
    return json as ApiResponse<T>;
  } catch (err: any) {
    reportNetworkError(method, path, err.message ?? 'Network error');
    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: err.message ?? 'Network error' },
    };
  }
}

/**
 * Authenticated fetch for non-JSON responses (e.g. replay video blobs).
 * Retries once after refreshing the access token on 401.
 */
export async function fetchAuthenticated(url: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };

  if (tokens?.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  let res = await fetch(url, { ...init, headers });

  let sessionExpired = false;

  if (res.status === 401 && tokens?.refreshToken) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${tokens!.accessToken}`;
      res = await fetch(url, { ...init, headers });
    } else {
      tokens = null;
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('user_profile');
      sessionExpired = true;
      reportSessionExpired(url);
      if (onSessionExpired) onSessionExpired();
    }
  }

  if (res.status >= 400 && !sessionExpired) {
    reportHttpFailure(init.method ?? 'GET', url, res.status);
  }

  return res;
}

/** Convenience helpers */
export const api = {
  get: <T>(path: string, options?: { signal?: AbortSignal }) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  postForm: <T>(path: string, formData: FormData) => requestForm<T>('POST', path, formData),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
